// Hook de acceso a API de Gestión Marcación (nuevo). Reutiliza los servicios existentes.
// No cambia endpoints ni contratos; solo normaliza errores y tipa respuestas.

import Gestion from 'src/services/gestionMarcacion';
import OperariosRemplazos from 'src/services/operarioRemplazo';
import Operarios from 'src/services/operarios';
import LoginAPI from 'src/services/login';

export interface ApiResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

export interface OperarioItem {
  nombre: string;
  legajo: number | string;
  [k: string]: any;
}

export interface RegistroMarcacion {
  nombre: string;
  nroLegajo: number | string;
  fecha: string;
  entrada: string | null;
  salida: string | null;
  total: string | number | null;
  idMarcacion: number;
  entrada_id?: number;
  salida_id?: number;
  tipoMarcacion: string;
  tipoHora: string;
  estado: string;
  nombrePServicio: string;
  psId?: number | string;
  // Id del registro HNP (para gestionar SIN-ASIGNACION).
  referencia_id?: string;
  [k: string]: any;
}

export interface CupoMarcacion {
  NORMA?: string | number;
  HNOCM?: string | number;
  DOMIN?: string | number;
  DONOC?: string | number;
  FERIA?: string | number;
  FENOC?: string | number;
  [k: string]: any;
}

export interface UsuarioInfo {
  nombre: string;
  legajo: number;
  cargo: string;
}

export interface FiltroGestion {
  fechaInicio: string;
  fechaFin: string;
  operario: string;
  estado: string;
  excluir: string[];
  nombrePs: string;
}

export interface RespuestaGestion {
  cupos: CupoMarcacion;
  datos: RegistroMarcacion[];
}

export interface HoraProcesada {
  tipohora: string;
  fecha: string;
  horario: string;
  nombre: string;
  NombrePServicio: string;
  legajo: number | string;
  cedula: string;
  pscodigo: string;
  puntoServicio: string;
  total: string;
  [k: string]: any;
}

export interface FiltroHorasProcesadas {
  fechaInicio: string;
  fechaFin: string;
  operario?: string;
  psID?: number | string;
  tipoHora?: string;
}

export interface RespuestaHorasProcesadas {
  info: HoraProcesada[];
  horas: {
    norma: string;
    hnoc: string;
    domin: string;
    donoc: string;
    feria: string;
    fenoc: string;
    cincuenta: string;
    [k: string]: string;
  };
}

export function useGestionMarcacionApi() {
  // Información del usuario logueado (para filtrar al fiscal en cuestión).
  const obtenerUsuario = async (): Promise<ApiResult<UsuarioInfo>> => {
    try {
      const response = await LoginAPI.me();
      const usuario = response.data.datos;
      return {
        ok: true,
        data: {
          nombre: usuario.emp_nombre || '',
          legajo: Number(usuario.emp_legajo) || 0,
          cargo: usuario.emp_cargo || '',
        },
      };
    } catch (e: any) {
      return { ok: false, error: 'No se pudo obtener el usuario logueado' };
    }
  };

  // Lista de operarios para autocomplete.
  const listarOperarios = async (): Promise<ApiResult<OperarioItem[]>> => {
    try {
      const lista = await Operarios.listarOperarios();
      const datos = Array.isArray(lista) ? (lista as OperarioItem[]) : [];
      return { ok: true, data: datos };
    } catch (e: any) {
      return { ok: false, error: 'No se pudo obtener la lista de operarios' };
    }
  };

  // Lista de puntos de servicio únicos.
  const listarPuntosServicio = async (): Promise<ApiResult<string[]>> => {
    try {
      const res: any = await OperariosRemplazos.listar();
      const arr: any[] = Array.isArray(res?.data?.datos) ? res.data.datos : [];
      const nombres = arr
        .map((d: any) => d?.ps_nombre)
        .filter((n: any) => typeof n === 'string' && n.length > 0);
      const unicos = Array.from(new Set(nombres));
      return { ok: true, data: unicos };
    } catch (e: any) {
      return { ok: false, error: 'No se pudo obtener los puntos de servicio' };
    }
  };

  // Filtrado principal de gestión de marcación.
  const filtrarGestion = async (filtro: FiltroGestion): Promise<ApiResult<RespuestaGestion>> => {
    try {
      const res: any = await Gestion.GestionAPI(filtro);
      const cupos = (res?.data?.cupos || {}) as CupoMarcacion;
      const datosRaw: RegistroMarcacion[] = Array.isArray(res?.data?.datos) ? res.data.datos : [];
      // Ocultar marcaciones REPETIDAS (estas se gestionan desde Gestión de Errores).
      const datos = datosRaw.filter((d) => {
        const estado = String(d?.estado || '').trim().toUpperCase();
        return !estado.startsWith('REPETID');
      });
      return { ok: true, data: { cupos, datos } };
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Error al filtrar gestión de marcación';
      return { ok: false, error: msg };
    }
  };

  // Consulta horas procesadas del rango/filtros indicados. El backend ya filtra por PS
  // asignados al fiscal logueado vía Operarios_asigfiscalpuntoservicio, no traemos nada de otros PS.
  const traerHorasProcesadas = async (filtro: FiltroHorasProcesadas): Promise<ApiResult<RespuestaHorasProcesadas>> => {
    try {
      const res: any = await Gestion.traerHorasProcesadas(filtro);
      const data = res?.data || {};
      return {
        ok: true,
        data: {
          info: Array.isArray(data.info) ? data.info : [],
          horas: data.horas || { norma: '00:00', hnoc: '00:00', domin: '00:00', donoc: '00:00', feria: '00:00', fenoc: '00:00', cincuenta: '00:00' },
        },
      };
    } catch (e: any) {
      if (e?.response?.status === 400) {
        // Backend retorna 400 cuando no encuentra registros; tratamos como lista vacía.
        return {
          ok: true,
          data: {
            info: [],
            horas: { norma: '00:00', hnoc: '00:00', domin: '00:00', donoc: '00:00', feria: '00:00', fenoc: '00:00', cincuenta: '00:00' },
          },
        };
      }
      return { ok: false, error: e?.response?.data?.error || 'No se pudieron obtener las horas procesadas' };
    }
  };

  // Lista todas las marcaciones reales (de ESME_EM_Marcaciones) del operario en una fecha.
  // Sin filtro de PS para dar contexto completo al fiscal en el modal de gestión.
  const traerMarcacionesDia = async (legajo: number | string, fecha: string): Promise<ApiResult<any[]>> => {
    try {
      const params = {
        operario: ` - ${legajo}`, // el backend hace split(" - ")[1] para extraer el legajo
        fechaInicio: fecha,
        fechaFin: fecha,
      };
      const res: any = await Gestion.traerMarcaciones(params);
      const arr = Array.isArray(res?.data) ? res.data : [];
      return { ok: true, data: arr };
    } catch (e: any) {
      if (e?.response?.status === 404) return { ok: true, data: [] };
      return { ok: false, error: e?.response?.data?.error || 'No se pudieron obtener las marcaciones del día' };
    }
  };

  const guardarEntrada = (payload: any) => Gestion.guardarEntrada(payload);
  const guardarSalida = (payload: any) => Gestion.guardarSalida(payload);
  const gestionarSinEntrada = (payload: any) => Gestion.gestionarSinEntrada(payload);
  const gestionarSinSalida = (payload: any) => Gestion.gestionarSinSalida(payload);
  const guardarSupera = (payload: any) => Gestion.guardarSupera(payload);
  const modificarMarcacion = (payload: any) => Gestion.modificarMarcacion(payload);
  const emparejarMarcaciones = (payload: { entrada_id: number; salida_id: number }) => Gestion.emparejarMarcaciones(payload);

  // Marcaciones del operario en un día, solo del PS indicado (para "Corregir par").
  const traerMarcacionesPs = async (legajo: number | string, fecha: string, psId: number | string): Promise<ApiResult<any[]>> => {
    try {
      const params = {
        operario: `LEG - ${legajo}`, // el backend hace split(" - ")[1] para extraer el legajo
        fechaInicio: fecha,
        fechaFin: fecha,
        nombrePuntoServicio: psId,
      };
      const res: any = await Gestion.traerMarcaciones(params);
      const arr = Array.isArray(res?.data) ? res.data : [];
      return { ok: true, data: arr };
    } catch (e: any) {
      if (e?.response?.status === 404) return { ok: true, data: [] };
      return { ok: false, error: e?.response?.data?.error || 'No se pudieron cargar las marcaciones.' };
    }
  };

  return {
    obtenerUsuario,
    listarOperarios,
    listarPuntosServicio,
    filtrarGestion,
    traerHorasProcesadas,
    traerMarcacionesDia,
    guardarEntrada,
    guardarSalida,
    gestionarSinEntrada,
    gestionarSinSalida,
    guardarSupera,
    modificarMarcacion,
    emparejarMarcaciones,
    traerMarcacionesPs,
  };
}
